import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, Weaver } from '@skylab-shared/core';
import { DocumentBuyerModel, DocumentClientModel, SaleDocumentAdjustmentModel } from '@skylab-shared/dataModel';
import { DocumentAdjustedBuyerNotSameTinError, DocumentAdjustedClientEmptyBuyerOnAdjusted, DocumentAdjustedClientEmptyBuyerOnAdjustment, DocumentAdjustedClientNotSameTinError } from '../../documents/violations/document-adjusted-client-errors';

interface DocumentModelPart extends ModelBase {
    client?: DocumentClientModel | null;
    buyer?: DocumentBuyerModel | null;
    seller?: DocumentClientModel | null;
    supplier?: DocumentBuyerModel | null;
}

export class DocumentClientAdjustmentValidationBoPart extends BusinessObjectPart<DocumentModelPart> {
    public constructor(
        weaver: Weaver,
        private adjustedDocument: () => DocumentModelPart
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerCoreFeatures();
    }

    private registerCoreFeatures(): void {
        const [clientProp, buyerProp] = this.bo.model instanceof SaleDocumentAdjustmentModel
            ? ['client', 'buyer'] as const
            : ['seller', 'supplier'] as const;

        this.engine.validation.addRule(
            [[DocumentClientModel, 'tin']],
            [[this.modelType, clientProp]],
            ctx => {
                if ((ctx.source !== this.bo.model[clientProp])) return true;

                return (this.bo.model[clientProp]?.tin?.trim() || '') === (this.adjustedDocument?.()?.[clientProp]?.tin?.trim() || '');
            },
            DocumentAdjustedClientNotSameTinError
        );

        this.engine.validation.addRule(
            [[DocumentClientModel, 'tin']],
            [[this.modelType, clientProp]],
            ctx => {
                if ((ctx.source !== this.bo.model[buyerProp])) return true;

                const currentBuyer = this.bo.model[buyerProp];
                if (!currentBuyer) return true;

                const adjustedBuyer = this.adjustedDocument()?.[buyerProp];
                if (!adjustedBuyer) return true;

                return (currentBuyer.tin?.trim() || '') === (adjustedBuyer.tin?.trim() || '');
            },
            DocumentAdjustedBuyerNotSameTinError
        );

        this.engine.validation.addRule(
            [[this.modelType, buyerProp]],
            [[this.modelType, clientProp]],
            _ => {
                const adjustedBuyer = this.adjustedDocument()?.[buyerProp];
                const currentBuyer = this.bo.model[buyerProp];

                if (!adjustedBuyer) return true;

                return !!adjustedBuyer === !!currentBuyer;
            },
            DocumentAdjustedClientEmptyBuyerOnAdjustment
        );

        this.engine.validation.addRule(
            [[this.modelType, buyerProp]],
            [[this.modelType, clientProp]],
            _ => {
                const adjustedBuyer = this.adjustedDocument()?.[buyerProp];
                const currentBuyer = this.bo.model[buyerProp];

                if (!currentBuyer) return true;

                return !!adjustedBuyer === !!currentBuyer;
            },
            DocumentAdjustedClientEmptyBuyerOnAdjusted
        );
    }
}
